import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmtINR, fmtDateTime, PAYMENT_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

const cell = "border border-gray-400 px-1.5 py-1";
const Section = ({ title, children }) => (
  <div className="mb-5 break-inside-avoid">
    <h2 className="text-sm font-bold uppercase tracking-wide border-b border-black pb-1 mb-2">{title}</h2>
    {children}
  </div>
);

const SummaryRow = ({ label, paise, strong, green, red }) => (
  <tr>
    <td className={`${cell} ${strong ? "font-bold" : ""}`}>{label}</td>
    <td className={`${cell} text-right font-mono ${strong ? "font-bold" : ""} ${green ? "text-green-700" : ""} ${red ? "text-red-700" : ""}`}>
      {paise === null || paise === undefined ? "—" : fmtINR(paise)}
    </td>
  </tr>
);

export default function PrintDayClosePage() {
  const [params] = useSearchParams();
  const storeId = params.get("store_id");
  const businessDate = params.get("business_date");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (storeId && businessDate)
      api.get("/print/day-close", { params: { store_id: storeId, business_date: businessDate } })
        .then((r) => setData(r.data)).catch((e) => setError(e));
  }, [storeId, businessDate]);

  if (error) return <div className="p-8 text-sm text-[hsl(var(--danger))]">Could not load the Day Close Report.</div>;
  if (!data) return <div className="p-8 text-sm text-muted-foreground">Preparing Day Close Report…</div>;

  const a = data.aggregate;
  const finalized = a.status === "finalized";

  return (
    <div className="print-preview min-h-screen text-black">
      <div className="no-print flex items-center justify-between p-4 border-b border-[hsl(var(--brass-dim)/0.5)] bg-[hsl(var(--ink))] text-[hsl(38_28%_92%)]">
        <Button asChild variant="ghost" size="sm"><Link to="/"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link></Button>
        <Button onClick={() => window.print()} data-testid="print-day-close-trigger-button"><Printer className="h-4 w-4 mr-1.5" />Print / Save PDF</Button>
      </div>

      <div className="max-w-[820px] mx-auto p-6" data-testid="day-close-report">
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-black pb-3 mb-4">
          <div>
            <h1 className="text-xl font-bold">{data.store?.name} — Day Close Report</h1>
            <p className="text-sm">Business date: {data.business_date}</p>
            <p className="text-xs mt-0.5">
              Status: <b>{finalized ? "FINALIZED" : "OPEN (provisional)"}</b>
              {finalized && a.finalized_by_name && <> — by {a.finalized_by_name}, {fmtDateTime(a.finalized_at)}</>}
            </p>
          </div>
          <div className="text-right text-xs">
            <p className="font-bold">Rokadly</p>
            <p>Printed {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Cash summary */}
          <Section title="Cash Summary">
            <table className="print-table w-full text-sm" data-testid="dc-cash-summary">
              <tbody>
                <SummaryRow label={`Opening cash${a.opening_source_date ? ` (from close of ${a.opening_source_date})` : ""}`} paise={a.opening_paise} />
                <SummaryRow label="Cash receipts (bills)" paise={a.cash_receipts_paise} />
                <SummaryRow label="Other cash receipts" paise={a.adj_cash_receipts_paise} />
                <SummaryRow label="Cash refunds / deductions" paise={-a.cash_refunds_paise} red />
                <SummaryRow label="Cash expenses" paise={-a.cash_expenses_paise} red />
                <SummaryRow label="Expected cash before HQ send" paise={a.expected_pre_hq_paise} strong />
                <SummaryRow label="Actual counted cash (pre-HQ)" paise={a.actual_pre_hq_paise} strong />
                <SummaryRow label="Variance" paise={a.variance_paise} strong
                  green={a.variance_paise > 0} red={a.variance_paise < 0} />
                <SummaryRow label="Cash sent to HQ from this close" paise={-(data.hq_sent_from_close_paise || 0)} red />
                <SummaryRow label="Carried to next day's opening" paise={data.next_opening_paise} strong />
              </tbody>
            </table>
            {a.count_stale && (
              <p className="text-xs mt-1 font-bold text-red-700" data-testid="dc-count-stale">
                The day's entries changed after this count was taken — it must be confirmed or corrected before finalization.
              </p>
            )}
            {a.opening_provisional && (
              <p className="text-xs mt-1 text-red-700" data-testid="dc-provisional">
                Opening is provisional — the close of {a.opening_source_date} is not finalized yet.
              </p>
            )}
          </Section>

          {/* Collections */}
          <Section title="Collections">
            <table className="print-table w-full text-sm" data-testid="dc-collections">
              <tbody>
                <SummaryRow label={`Bill total (${a.bill_count} bills)`} paise={a.bill_total_paise} strong />
                <SummaryRow label="Cash" paise={a.cash_receipts_paise} />
                <SummaryRow label="Card" paise={a.card_paise} />
                <SummaryRow label="Cheque" paise={a.cheque_paise} />
                <SummaryRow label="Bank" paise={a.bank_paise} />
                <SummaryRow label="Other" paise={a.other_paise} />
                <SummaryRow label="Less taken" paise={a.less_taken_paise} red />
                {a.excess_kept_paise > 0 && <SummaryRow label="Extra received (kept)" paise={a.excess_kept_paise} green />}
                {a.bank_refunds_paise > 0 && <SummaryRow label="Excess returned via bank" paise={-a.bank_refunds_paise} red />}
              </tbody>
            </table>
          </Section>
        </div>

        {/* Cashier-wise collections + store closing count */}
        <Section title="Cashier-wise Collections (shared store drawer)">
          <table className="print-table w-full text-sm" data-testid="dc-cashiers">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className={cell}>Cashier</th>
                <th className={`${cell} text-right`}>Bills</th>
                <th className={`${cell} text-right`}>Bill total</th>
                <th className={`${cell} text-right`}>Cash In</th>
                <th className={`${cell} text-right`}>Non-cash</th>
                <th className={`${cell} text-right`}>Less taken</th>
                <th className={`${cell} text-right`}>Cash expenses</th>
              </tr>
            </thead>
            <tbody>
              {data.cashiers.length === 0 && <tr><td colSpan={7} className={`${cell} text-center text-gray-500`}>No cashier activity</td></tr>}
              {data.cashiers.map((c) => (
                <tr key={c.cashier_id}>
                  <td className={cell}>{c.cashier_name}</td>
                  <td className={`${cell} text-right font-mono`}>{c.bill_count}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(c.bill_total_paise)}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(c.cash_from_bills_paise - c.cash_excess_returned_paise + c.adj_cash_receipts_paise)}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(c.noncash_from_bills_paise)}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(c.less_taken_paise)}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(c.cash_expenses_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs mt-2" data-testid="dc-store-count">
            <b>Store closing count (pre-HQ):</b>{" "}
            {data.count
              ? <>{fmtINR(data.count.counted_paise)} counted vs {fmtINR(data.count_state?.current_expected_pre_hq_paise ?? data.count.expected_paise)} expected — variance {fmtINR(data.count_state?.current_variance_paise ?? data.count.variance_paise, { signed: true })}; submitted by {data.count.submitted_by_name}{data.count.note ? ` (${data.count.note})` : ""}</>
              : "not submitted"}
          </p>
        </Section>

        {/* Cashier-wise shortage record */}
        {data.shortage_record?.total_shortage_paise > 0 && (
          <Section title="Cashier-wise Shortage Record">
            {data.shortage_record.review_needed && (
              <p className="text-xs font-bold text-red-700 mb-1" data-testid="dc-shortage-review">
                PENDING REVIEW — {data.shortage_record.review_message} The earlier cashier-wise split is not
                printed because it no longer matches the current shortage.
              </p>
            )}
            <table className="print-table w-full text-sm" data-testid="dc-shortage-record">
              <thead>
                <tr className="text-left text-xs uppercase">
                  <th className={cell}>Cashier</th>
                  <th className={cell}>Store / Date</th>
                  <th className={cell}>Note</th>
                  <th className={`${cell} text-right`}>Shortage assigned</th>
                </tr>
              </thead>
              <tbody>
                {data.shortage_record.allocations.map((al, i) => (
                  <tr key={`${al.cashier_id}-${i}`}>
                    <td className={cell}>{al.cashier_name}</td>
                    <td className={`${cell} text-xs`}>{data.store?.name} · {data.business_date}</td>
                    <td className={`${cell} text-xs`}>{al.note || "—"}</td>
                    <td className={`${cell} text-right font-mono`}>{fmtINR(al.amount_paise)}</td>
                  </tr>
                ))}
                {data.shortage_record.unallocated_paise > 0 && (
                  <tr>
                    <td className={`${cell} font-bold`}>
                      {data.shortage_record.review_needed ? "Awaiting review — not assigned" : "Unallocated — under investigation"}
                    </td>
                    <td className={`${cell} text-xs`}>{data.store?.name} · {data.business_date}</td>
                    <td className={`${cell} text-xs`}>Responsibility not assigned yet</td>
                    <td className={`${cell} text-right font-mono font-bold`}>{fmtINR(data.shortage_record.unallocated_paise)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className={`${cell} font-bold text-right`}>Total shortage of the day</td>
                  <td className={`${cell} text-right font-mono font-bold`}>{fmtINR(data.shortage_record.total_shortage_paise)}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs mt-1">
              A record of responsibility only — it is not a cash receipt and does not by itself mean the money was recovered.
            </p>
          </Section>
        )}

        {/* HQ remittances from this close */}
        {data.hq_sends?.length > 0 && (
          <Section title="Cash Sent to HQ from this Close">
            <table className="print-table w-full text-sm" data-testid="dc-hq-sends">
              <thead>
                <tr className="text-left text-xs uppercase">
                  <th className={cell}>Entered on</th>
                  <th className={cell}>By</th>
                  <th className={cell}>Note</th>
                  <th className={`${cell} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.hq_sends.map((t) => (
                  <tr key={t.id}>
                    <td className={`${cell} text-xs`}>{fmtDateTime(t.entered_at)}</td>
                    <td className={`${cell} text-xs`}>{t.by || "—"}</td>
                    <td className={`${cell} text-xs`}>{t.note || "—"}</td>
                    <td className={`${cell} text-right font-mono`}>{fmtINR(t.amount_paise)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className={`${cell} font-bold text-right`}>Remaining for the next day's opening</td>
                  <td className={`${cell} text-right font-mono font-bold`}>{fmtINR(data.next_opening_paise)}</td>
                </tr>
              </tbody>
            </table>
          </Section>
        )}

        {/* Non-cash summary */}
        <Section title="Non-Cash Collections Summary">
          <table className="print-table w-full text-sm" data-testid="dc-noncash">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className={cell}>Group</th>
                <th className={`${cell} text-right`}>Entries</th>
                <th className={`${cell} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody>
              {data.noncash_groups.length === 0 && <tr><td colSpan={3} className={`${cell} text-center text-gray-500`}>No non-cash entries</td></tr>}
              {data.noncash_groups.map((g) => (
                <tr key={g.group_key}>
                  <td className={cell}>{g.group_label}</td>
                  <td className={`${cell} text-right font-mono`}>{g.count}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(g.total_paise)}</td>
                </tr>
              ))}
              {data.noncash_groups.length > 0 && (
                <tr>
                  <td className={`${cell} font-bold text-right`} colSpan={2}>Total non-cash</td>
                  <td className={`${cell} text-right font-mono font-bold`}>{fmtINR(data.noncash_total_paise)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>

        {/* Expenses */}
        <Section title="Expenses & Business Payments">
          <table className="print-table w-full text-sm" data-testid="dc-expenses">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className={cell}>Description</th>
                <th className={cell}>Nature</th>
                <th className={cell}>Voucher</th>
                <th className={cell}>Mode</th>
                <th className={cell}>By</th>
                <th className={`${cell} text-right`}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.expenses.length === 0 && <tr><td colSpan={6} className={`${cell} text-center text-gray-500`}>No expenses</td></tr>}
              {data.expenses.map((e) => (
                <tr key={e.id}>
                  <td className={cell}>{e.description}{e.head_name ? ` (${e.head_name})` : ""}</td>
                  <td className={`${cell} text-xs`}>{e.nature === "business_payment" ? "Business" : "Operating"}</td>
                  <td className={`${cell} text-xs`}>{e.voucher_status === "with_voucher" ? (e.voucher_no || "yes") : "no voucher"}</td>
                  <td className={`${cell} text-xs uppercase`}>{e.payment_type}{e.bank_name ? `·${e.bank_name}` : ""}</td>
                  <td className={`${cell} text-xs`}>{e.cashier_name}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(e.amount_paise)}</td>
                </tr>
              ))}
              {data.expenses.length > 0 && (
                <tr>
                  <td colSpan={5} className={`${cell} font-bold text-right`}>Total expenses</td>
                  <td className={`${cell} text-right font-mono font-bold`}>{fmtINR(data.expenses.reduce((s, e) => s + e.amount_paise, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>

        {/* Adjustments */}
        {data.adjustments.length > 0 && (
          <Section title="Other Receipts / Deductions">
            <table className="print-table w-full text-sm" data-testid="dc-adjustments">
              <thead>
                <tr className="text-left text-xs uppercase">
                  <th className={cell}>Description</th>
                  <th className={cell}>Kind</th>
                  <th className={cell}>Mode</th>
                  <th className={cell}>By</th>
                  <th className={`${cell} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.adjustments.map((adj) => (
                  <tr key={adj.id}>
                    <td className={cell}>{adj.description}{adj.related_bill_no ? ` (bill ${adj.related_bill_no})` : ""}</td>
                    <td className={`${cell} text-xs uppercase`}>{adj.kind}</td>
                    <td className={`${cell} text-xs`}>{PAYMENT_LABELS[adj.payment_type]}{adj.bank_name ? `·${adj.bank_name}` : ""}</td>
                    <td className={`${cell} text-xs`}>{adj.cashier_name}</td>
                    <td className={`${cell} text-right font-mono ${adj.kind === "deduction" ? "text-red-700" : ""}`}>
                      {adj.kind === "deduction" ? "−" : ""}{fmtINR(adj.amount_paise)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* Discrepancies */}
        <Section title="Discrepancy Notes">
          <table className="print-table w-full text-sm" data-testid="dc-discrepancies">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className={cell}>Type</th>
                <th className={`${cell} text-right`}>Amount</th>
                <th className={`${cell} text-right`}>Settled</th>
                <th className={cell}>Status</th>
                <th className={cell}>Responsibility</th>
                <th className={cell}>Note</th>
              </tr>
            </thead>
            <tbody>
              {data.discrepancies.length === 0 && <tr><td colSpan={6} className={`${cell} text-center text-gray-500`}>No discrepancies — day balanced</td></tr>}
              {data.discrepancies.map((d) => (
                <tr key={d.id}>
                  <td className={`${cell} uppercase text-xs font-bold ${d.type === "shortage" ? "text-red-700" : "text-green-700"}`}>{d.type}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(d.amount_paise)}</td>
                  <td className={`${cell} text-right font-mono`}>{fmtINR(d.settled_paise || 0)}</td>
                  <td className={`${cell} text-xs`}>{(d.status || "").replace(/_/g, " ")}</td>
                  <td className={`${cell} text-xs`}>
                    {(d.allocations || []).map((al) => `${al.cashier_name} ${fmtINR(al.amount_paise)}`).join(", ") || "—"}
                    {d.unallocated_paise > 0 && <span className="block font-bold">Unallocated {fmtINR(d.unallocated_paise)}</span>}
                  </td>
                  <td className={`${cell} text-xs`}>{d.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Pending cheques */}
        {data.pending_cheques.length > 0 && (
          <Section title="Pending Cheques Received Today">
            <table className="print-table w-full text-sm" data-testid="dc-cheques">
              <thead>
                <tr className="text-left text-xs uppercase">
                  <th className={cell}>Cheque No</th>
                  <th className={cell}>Name</th>
                  <th className={cell}>Bill</th>
                  <th className={cell}>Due</th>
                  <th className={`${cell} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_cheques.map((c) => (
                  <tr key={c.id}>
                    <td className={`${cell} font-mono`}>{c.cheque_no}</td>
                    <td className={cell}>{c.name_on_cheque || "—"}</td>
                    <td className={`${cell} font-mono`}>{c.bill_no || "—"}</td>
                    <td className={`${cell} text-xs`}>{c.due_date || "—"}</td>
                    <td className={`${cell} text-right font-mono`}>{fmtINR(c.amount_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <div className="flex justify-between mt-10 pt-6 text-sm">
          <div className="border-t border-black w-[200px] text-center pt-1">Cashier signature</div>
          <div className="border-t border-black w-[200px] text-center pt-1">Manager / Admin signature</div>
        </div>
      </div>
    </div>
  );
}
